/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../../shared/theme/colors'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { styles } from '../posShared.styles';
import { isFractionalCartLine } from '../../store/usePosStore';
import type { CartItem } from '../../store/usePosStore';
import { formatOptionsLabel } from '../../../catalog/domain-api';
import { formatUnitLabel } from '../../../catalog/domain-api';
import { formatQty } from '@shared/quantity';
import { parseDecimalInput, inputHasDecimalSeparator } from '../../../shared/numeric/public-api';
import type { EvaluatedCartLine } from '../../../promotions/public-api';
import { formatPosLinePriceBlock, formatPosLinePromoHint } from '../../../promotions/public-api';
import { CART_LINE_STOCK_HINT, getStockWarningSeverity } from '../../application/posCartStockIssuePresentation';
import type { PosStockWarningDetail } from '../../domain/posTypes';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import { OperationalHintIcon } from '../../../shared/ui/OperationalHintIcon';
import { formatMoney } from '../../../shared/format/public-api';
import { usePosCartContextOptional } from '../contexts/PosCartContext';
import { PosCartLineAttributionPicker } from './PosCartLineAttributionPicker';
import { PosCartLineNoteModal } from '../modals/PosCartLineNoteModal';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';

type PosCartLineRowProps = {
  item: CartItem;
  index: number;
  isLast: boolean;
  isCompactCart: boolean;
  inSheet?: boolean;
  /** Línea visual ticket (mobile sheet o panel PC). */
  ticketLayout?: boolean;
  selected?: boolean;
  onSelect?: (lineId: string | null) => void;
  evaluatedLine?: EvaluatedCartLine;
  stockWarning?: PosStockWarningDetail;
  /** Composición de combo de catálogo ("2× Papas · 1× Coca"). */
  comboLabel?: string | null;
  canEdit?: boolean;
  onRemoveFromCart?: (lineId: string) => void;
  onIncrementCartItem?: (lineId: string) => void;
  onSetCartLineQuantity?: (lineId: string, quantity: number) => void;
  onRemoveCartLine?: (lineId: string) => void;
  onSetCartLineAttribution?: (lineId: string, performedByUserId: string | null, soldByUserId: string | null) => void;
  onSetCartLineNote?: (lineId: string, note: string | null) => void;
  /** Gap 4: re-editar menu options de una línea existente. */
  onOpenMenuOptionsEditor?: (line: CartItem) => void;
  /** Gap 4: re-editar variante de una línea existente. */
  onOpenVariantEditor?: (line: CartItem) => void;
  /** Gap 5: editar nombre/monto de una venta Varios (custom) existente. */
  onOpenVariosEditor?: (line: CartItem) => void;
};

function formatUnitPrice(item: CartItem): string {
  const p = formatMoney(item.price);
  if (item.itemType === 'custom') {
    return `${p} · libre`;
  }
  if (item.itemType === 'package') {
    return `${p} · paquete`;
  }
  if (item.catalogItemType === 'service') {
    return `${p} · servicio`;
  }
  if (isFractionalCartLine(item)) {
    const unitLabel = formatUnitLabel(item.unitOfMeasure ?? 'unit');
    return `${p} / ${unitLabel}`;
  }
  return `${p} c/u`;
}

function formatCartLineQuantityLabel(item: CartItem): string {
  if (!isFractionalCartLine(item)) {
    return String(item.quantity);
  }
  return formatQty(item.quantity, item.unitOfMeasure ?? 'unit');
}

function formatTicketQty(item: CartItem): string {
  if (!isFractionalCartLine(item)) {
    return String(item.quantity);
  }
  return formatQty(item.quantity, item.unitOfMeasure ?? 'unit').split(' ')[0];
}

function CartLineControls({
  item,
  canEdit,
  compact,
  ticket,
  onRemoveFromCart,
  onIncrementCartItem,
  onSetCartLineQuantity,
  onRemoveCartLine,
}: {
  item: CartItem;
  canEdit: boolean;
  compact?: boolean;
  ticket?: boolean;
  onRemoveFromCart: (lineId: string) => void;
  onIncrementCartItem: (lineId: string) => void;
  onSetCartLineQuantity: (lineId: string, quantity: number) => void;
  onRemoveCartLine: (lineId: string) => void;
}) {
  const fractional = isFractionalCartLine(item);
  // En unidades enteras el sanitizador descarta el separador en silencio
  // ("1,5" → "15"). Marcamos la línea con un hint, como PhysicalCountModal.
  const [integerQtyHint, setIntegerQtyHint] = useState(false);

  const onQtyChange = (raw: string) => {
    if (raw === '') return;
    if (fractional) {
      const n = parseDecimalInput(raw, { min: 0.001 });
      if (n !== null) onSetCartLineQuantity(item.lineId, n);
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1) onSetCartLineQuantity(item.lineId, n);
  };

  const onQtyRawChange = (raw: string) => {
    if (!fractional && inputHasDecimalSeparator(raw) && !integerQtyHint) {
      setIntegerQtyHint(true);
    }
  };

  const integerQtyHintEl = integerQtyHint ? (
    <Text style={styles.cartQtyIntegerHint} testID="pos.cart.line.qty-integer-hint">
      Este producto se vende en unidades enteras.
    </Text>
  ) : null;

  if (ticket) {
    return (
      <View>
        <View style={styles.cartTicketEditRow}>
          <TouchableOpacity
            onPress={() => onRemoveCartLine(item.lineId)}
            style={styles.cartTicketRemoveBtn}
            disabled={!canEdit}
            accessibilityLabel="Quitar línea"
            testID="pos.cart.line.remove"
          >
            <Text style={styles.cartTicketRemoveText}>Quitar</Text>
          </TouchableOpacity>
          <View style={styles.cartTicketStepper}>
            <TouchableOpacity
              onPress={() => onRemoveFromCart(item.lineId)}
              style={styles.cartTicketStepBtn}
              disabled={!canEdit}
              accessibilityLabel="Restar cantidad"
              testID="pos.cart.line.decrement"
            >
              <Text style={styles.cartTicketStepLabel}>−</Text>
            </TouchableOpacity>
            <NumericTextInput
              kind={fractional ? 'decimalMoney' : 'integer'}
              value={String(item.quantity)}
              onChangeText={onQtyChange}
              onRawChangeText={onQtyRawChange}
              editable={canEdit}
              selectTextOnFocus
              style={styles.cartTicketQtyInput}
              accessibilityLabel="Cantidad"
              onBlur={() => {
                const parsed = parseDecimalInput(String(item.quantity), { min: 0.0001 });
                if (parsed == null) onSetCartLineQuantity(item.lineId, 1);
              }}
            />
            <TouchableOpacity
              onPress={() => onIncrementCartItem(item.lineId)}
              style={styles.cartTicketStepBtn}
              disabled={!canEdit}
              accessibilityLabel="Sumar cantidad"
              testID="pos.cart.line.increment"
            >
              <Text style={styles.cartTicketStepLabel}>+</Text>
            </TouchableOpacity>
          </View>
        </View>
        {integerQtyHintEl}
      </View>
    );
  }

  return (
    <View>
      <View style={[styles.cartCanvasControls, compact && styles.cartCanvasControlsCompact]}>
        <TouchableOpacity
          onPress={() => onRemoveFromCart(item.lineId)}
          style={[styles.cartActionButton, compact && styles.cartActionButtonCompact]}
          disabled={!canEdit}
          testID="pos.cart.line.decrement"
        >
          <Text style={[styles.cartActionLabel, compact && styles.cartActionLabelCompact]}>−</Text>
        </TouchableOpacity>
        <NumericTextInput
          kind={fractional ? 'decimalMoney' : 'integer'}
          value={String(item.quantity)}
          onChangeText={onQtyChange}
          onRawChangeText={onQtyRawChange}
          editable={canEdit}
          selectTextOnFocus
          style={[styles.cartQuantityInput, compact && styles.cartQuantityInputCompact]}
          accessibilityLabel="Cantidad"
        />
        <TouchableOpacity
          onPress={() => onIncrementCartItem(item.lineId)}
          style={[styles.cartActionButton, compact && styles.cartActionButtonCompact]}
          disabled={!canEdit}
          testID="pos.cart.line.increment"
        >
          <Text style={[styles.cartActionLabel, compact && styles.cartActionLabelCompact]}>+</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => onRemoveCartLine(item.lineId)}
          style={[styles.cartTrashButton, compact && styles.cartTrashButtonCompact]}
          disabled={!canEdit}
          accessibilityLabel="Quitar línea"
          testID="pos.cart.line.remove"
        >
          <Ionicons name="trash-outline" size={compact ? 14 : 16} color={colors.danger} />
        </TouchableOpacity>
      </View>
      {integerQtyHintEl}
    </View>
  );
}

export function PosCartLineRow(props: PosCartLineRowProps) {
  const ctx = usePosCartContextOptional();
  const {
    item,
    isLast,
    isCompactCart,
    inSheet = false,
    ticketLayout = false,
    selected = false,
    onSelect,
    evaluatedLine,
    stockWarning,
    comboLabel = null,
    canEdit = false,
    onRemoveFromCart = () => {},
    onIncrementCartItem = () => {},
    onSetCartLineQuantity = () => {},
    onRemoveCartLine = () => {},
    onSetCartLineAttribution = () => {},
    onSetCartLineNote = () => {},
    onOpenMenuOptionsEditor = () => {},
    onOpenVariantEditor = () => {},
    onOpenVariosEditor = () => {},
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const useTicket = ticketLayout || inSheet;
  const fallbackGross = item.price * item.quantity;
  const priceBlock = formatPosLinePriceBlock(evaluatedLine, fallbackGross);
  const [attributionPickerVisible, setAttributionPickerVisible] = useState(false);
  const [noteEditorVisible, setNoteEditorVisible] = useState(false);

  const stockHintIcon = stockWarning ? (
    <OperationalHintIcon
      title="Stock"
      message={CART_LINE_STOCK_HINT}
      severity={getStockWarningSeverity(stockWarning)}
      size={useTicket ? 14 : isCompactCart ? 14 : 16}
      testID="pos-cart-line-stock-hint"
    />
  ) : null;

  const hasCommissions = useHasBusinessCapability('commissions');
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = withSelfOwner(employeesRaw, selfOwner);

  const isService = item.catalogItemType === 'service';
  const attributionUserId = isService
    ? item.performedByUserId ?? null
    : item.performedByUserId ?? item.soldByUserId ?? null;
  const hasAttribution = !!attributionUserId;
  const isUnassignedService = hasCommissions && isService && !attributionUserId;

  const employeeInitials = (userId: string | null): string => {
    if (!userId) return '?';
    const emp = employees.find((e) => e.authUserId === userId);
    if (!emp?.name) return '?';
    const parts = emp.name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  };

  const attributionBtn = canEdit && hasCommissions ? (
    <TouchableOpacity
      onPress={() => setAttributionPickerVisible(true)}
      style={styles.cartAttributionBtn}
      accessibilityLabel={hasAttribution ? `Asignado a ${employeeInitials(attributionUserId)}` : 'Asignar comisión'}
      testID="pos.cart.line.attribution"
    >
      {attributionUserId ? (
        <View style={cartAttrStyles.avatar}>
          <Text style={cartAttrStyles.avatarText}>{employeeInitials(attributionUserId)}</Text>
        </View>
      ) : isUnassignedService ? (
        <View style={cartAttrStyles.iconWrap}>
          <Ionicons
            name="person-add-outline"
            size={useTicket ? 16 : isCompactCart ? 14 : 18}
            color={ticketColors.warn}
          />
          <View style={cartAttrStyles.dot} />
        </View>
      ) : (
        <Ionicons
          name="person-add-outline"
          size={useTicket ? 16 : isCompactCart ? 14 : 18}
          color={colors.textMuted}
        />
      )}
    </TouchableOpacity>
  ) : null;

  const attributionPicker = (
    <PosCartLineAttributionPicker
      visible={attributionPickerVisible}
      lineId={item.lineId}
      itemName={item.name}
      currentPerformedByUserId={item.performedByUserId ?? null}
      currentSoldByUserId={item.soldByUserId ?? null}
      onClose={() => setAttributionPickerVisible(false)}
      onApply={onSetCartLineAttribution}
    />
  );

  const modsLabel = formatOptionsLabel(item.menuOptions);
  const noteLabel = item.lineNote?.trim() || '';
  const promoHint = formatPosLinePromoHint(evaluatedLine?.appliedDiscounts ?? []);
  const hasMenuOptions = !!(item.menuOptions && item.menuOptions.length > 0);
  const hasVariant = !!item.variantId;
  const isCustom = item.itemType === 'custom';
  const canEditLine = canEdit && (hasMenuOptions || hasVariant || isCustom);

  const editBtn = canEditLine ? (
    <TouchableOpacity
      onPress={() => {
        if (isCustom) {
          onOpenVariosEditor(item);
        } else if (hasMenuOptions) {
          onOpenMenuOptionsEditor(item);
        } else {
          onOpenVariantEditor(item);
        }
      }}
      style={styles.cartAttributionBtn}
      accessibilityLabel={isCustom ? 'Editar venta rápida' : 'Editar adicionales o variante'}
      testID="pos.cart.line.edit"
    >
      <Ionicons
        name="pencil-outline"
        size={useTicket ? 16 : isCompactCart ? 14 : 18}
        color={ticketColors.brandInk}
      />
    </TouchableOpacity>
  ) : null;

  if (useTicket) {
    const unitHint = isFractionalCartLine(item)
      ? ` · ${formatUnitLabel(item.unitOfMeasure ?? 'unit')}`
      : item.itemType === 'package'
        ? ' · paquete'
        : item.catalogItemType === 'service'
          ? ' · servicio'
          : '';
    /** Mobile sheet: expandir al tocar. PC desk: controles siempre visibles. */
    const showControls = inSheet ? selected : true;
    const nameLines = inSheet ? (selected ? 2 : 1) : 2;

    const lineStyle = [
      styles.cartCanvasLine,
      styles.cartTicketLine,
      selected && inSheet && styles.cartTicketLineActive,
      isLast && !(selected && inSheet) && styles.cartRowLast,
    ];
    const qtyEl = <Text style={styles.cartTicketQty}>{formatTicketQty(item)}</Text>;
    const nameCol = (
      <View style={styles.cartTicketNameCol}>
        <Text style={styles.cartTicketName} numberOfLines={nameLines}>
          {item.name}
          {unitHint ? (
            <Text style={{ color: colors.textMuted, fontWeight: '400' }}>{unitHint}</Text>
          ) : null}
        </Text>
        {modsLabel ? (
          <Text style={styles.cartTicketMods} numberOfLines={2}>
            {modsLabel}
          </Text>
        ) : null}
        {comboLabel ? (
          <Text style={styles.cartTicketMods} numberOfLines={1}>
            {`Incluye: ${comboLabel}`}
          </Text>
        ) : null}
        {promoHint ? (
          <Text style={styles.cartTicketMods} numberOfLines={1}>
            {`⌁ ${promoHint}`}
          </Text>
        ) : null}
        {noteLabel ? (
          <Text style={styles.cartTicketNote} numberOfLines={1}>
            {noteLabel}
          </Text>
        ) : null}
      </View>
    );

    const noteBtn = canEdit ? (
      <TouchableOpacity
        onPress={() => setNoteEditorVisible(true)}
        style={styles.cartAttributionBtn}
        accessibilityLabel={noteLabel ? 'Editar nota' : 'Agregar nota'}
        testID="pos.cart.line.note"
      >
        <Ionicons
          name={noteLabel ? 'chatbubble-outline' : 'chatbubble-ellipses-outline'}
          size={useTicket ? 16 : isCompactCart ? 14 : 18}
          color={noteLabel ? ticketColors.brandInk : colors.textMuted}
        />
      </TouchableOpacity>
    ) : null;

    return (
      <>
      <View testID="pos-cart-line" style={lineStyle}>
        <View style={styles.cartTicketRow}>
          {inSheet && onSelect ? (
            <Pressable
              onPress={() => onSelect(selected ? null : item.lineId)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              style={styles.cartTicketSelectHit}
            >
              {qtyEl}
              {nameCol}
            </Pressable>
          ) : (
            <>
              {qtyEl}
              {nameCol}
            </>
          )}
          <View style={styles.cartTicketEndCol}>
            {stockHintIcon}
            {editBtn}
            {noteBtn}
            {attributionBtn}
            <View style={styles.cartTicketPriceCol}>
              {priceBlock.hasDiscount ? (
                <Text style={styles.cartTicketListPrice}>{priceBlock.listLabel}</Text>
              ) : null}
              <Text style={styles.cartTicketPrice}>{priceBlock.netLabel}</Text>
            </View>
          </View>
        </View>
        {showControls ? (
          <CartLineControls
            item={item}
            canEdit={canEdit}
            ticket
            onRemoveFromCart={onRemoveFromCart}
            onIncrementCartItem={onIncrementCartItem}
            onSetCartLineQuantity={onSetCartLineQuantity}
            onRemoveCartLine={onRemoveCartLine}
          />
        ) : null}
      </View>
      {attributionPicker}
      <PosCartLineNoteModal
        visible={noteEditorVisible}
        lineId={item.lineId}
        itemName={item.name}
        currentNote={item.lineNote ?? null}
        onClose={() => setNoteEditorVisible(false)}
        onSave={onSetCartLineNote}
      />
    </>
  );
  }

  const qtyMeta = `${formatCartLineQuantityLabel(item)} × ${formatUnitPrice(item)}`;

  return (
    <View
      testID="pos-cart-line"
      style={[styles.cartCanvasLine, styles.cartCanvasLineDense, isLast && styles.cartRowLast]}
    >
      <View style={styles.cartLineTopRow}>
        <View style={styles.cartLineNameBlock}>
          <Text style={[styles.cartNameCompact, styles.cartLineNameText]} numberOfLines={1}>
            {item.name}
          </Text>
          {modsLabel ? (
            <Text style={[styles.cartLineMods, styles.cartLineMetaText]} numberOfLines={1}>
              {modsLabel}
            </Text>
          ) : null}
          {comboLabel ? (
            <Text style={[styles.cartLineMods, styles.cartLineMetaText]} numberOfLines={1}>
              {`Incluye: ${comboLabel}`}
            </Text>
          ) : null}
          {promoHint ? (
            <Text style={[styles.cartLineMods, styles.cartLineMetaText]} numberOfLines={1}>
              {`⌁ ${promoHint}`}
            </Text>
          ) : null}
          {noteLabel ? (
            <Text style={[styles.cartLineNote, styles.cartLineMetaText]} numberOfLines={1}>
              {noteLabel}
            </Text>
          ) : null}
          <Text
            style={[styles.cartPriceMeta, styles.cartPriceMetaDense, styles.cartLineMetaText]}
            numberOfLines={1}
          >
            {qtyMeta}
          </Text>
        </View>
        <Text
          style={[styles.cartLineSubtotal, isCompactCart && styles.cartLineSubtotalCompact]}
          numberOfLines={1}
        >
          {priceBlock.netLabel}
        </Text>
      </View>
      <View style={styles.cartLineBottomRow}>
        <CartLineControls
          item={item}
          canEdit={canEdit}
          compact={isCompactCart}
          onRemoveFromCart={onRemoveFromCart}
          onIncrementCartItem={onIncrementCartItem}
          onSetCartLineQuantity={onSetCartLineQuantity}
          onRemoveCartLine={onRemoveCartLine}
        />
        <View style={styles.cartLineSignalsRow}>
          <View style={styles.cartLineStockHintSlot}>{stockHintIcon}</View>
          {editBtn}
          {canEdit ? (
            <TouchableOpacity
              onPress={() => setNoteEditorVisible(true)}
              style={styles.cartAttributionBtn}
              accessibilityLabel={noteLabel ? 'Editar nota' : 'Agregar nota'}
              testID="pos.cart.line.note"
            >
              <Ionicons
                name={noteLabel ? 'chatbubble-outline' : 'chatbubble-ellipses-outline'}
                size={isCompactCart ? 14 : 16}
                color={noteLabel ? ticketColors.brandInk : colors.textMuted}
              />
            </TouchableOpacity>
          ) : null}
          {attributionBtn}
        </View>
      </View>
      {attributionPicker}
      <PosCartLineNoteModal
        visible={noteEditorVisible}
        lineId={item.lineId}
        itemName={item.name}
        currentNote={item.lineNote ?? null}
        onClose={() => setNoteEditorVisible(false)}
        onSave={onSetCartLineNote}
      />
    </View>
  );
}

const cartAttrStyles = StyleSheet.create({
  avatar: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.brandBorderSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 9,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  iconWrap: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dot: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: ticketColors.warn,
    borderWidth: 1.5,
    borderColor: ticketColors.paper,
  },
});
